import { useEffect, useState } from "react";
import { getCachedPhotoUrl } from "@/utils/photoCache";

/**
 * Route preview thumbnail with a graceful, themed fallback AND a
 * persistent client-side cache.
 *
 * Caching: route photos are routed through `getCachedPhotoUrl`, which
 * keeps a Map + IndexedDB blob store keyed by URL. Bundled static assets
 * pass through untouched (the bundler+browser already cache those
 * optimally); remote URLs (Supabase storage, future Google Places photo
 * lookups, etc.) get fetched once and reused across reloads.
 *
 * Fallback: if the real photo fails to load — broken bundle, blocked
 * CDN, expired signed URL, etc. — we render a small themed SVG instead
 * of a broken-image icon.
 */
export type RoutePhotoVariant = "coastal" | "freeway" | "generic";

/**
 * RoutePhoto API contract:
 *   - `src` / `variant` configure the source + fallback look.
 *   - `alt` is the accessible name (REQUIRED for both photo + placeholder).
 *   - `className` is for *cosmetic* overrides only (extra rounding, ring,
 *     margin, etc.). Do NOT pass sizing utilities like `w-*`, `h-*`,
 *     `aspect-*`, or `object-*` — sizing is owned by the wrapper so the
 *     reserved space stays identical across loading / error / loaded
 *     states and across breakpoints.
 */
interface Props {
  /** Image source. When omitted, the component renders its themed route
   *  diagram instead — an honest illustration, never a fake photo. */
  src?: string;
  alt: string;
  variant: RoutePhotoVariant;
  /** Cosmetic-only overrides (rounding, ring, margin). No sizing utilities. */
  className?: string;
}

export function RoutePhoto({ src, alt, variant, className = "" }: Props) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setResolvedSrc(null);
    // No src → fall straight through to the themed diagram.
    if (!src) { setErrored(true); return; }
    setErrored(false);
    getCachedPhotoUrl(src)
      .then((u) => { if (!cancelled) setResolvedSrc(u); })
      .catch(() => { if (!cancelled) setErrored(true); });
    return () => { cancelled = true; };
  }, [src]);

  // Wrapper enforces a single, predictable aspect ratio + min-height
  // across all callers so the photo never pushes the surrounding text
  // by a different amount on small vs large screens. Inner media is
  // absolutely positioned and always object-cover.
  const wrapperCls = `relative w-full overflow-hidden rounded-md bg-muted aspect-[16/10] min-h-[4rem]${className ? ` ${className}` : ""}`;

  if (errored || !resolvedSrc) {
    return (
      <div className={wrapperCls}>
        <ThemedPlaceholder variant={variant} alt={alt} className="absolute inset-0 size-full object-cover" />
      </div>
    );
  }

  return (
    <div className={wrapperCls}>
      <img
        src={resolvedSrc}
        alt={alt}
        loading="lazy"
        width={768}
        height={480}
        className="absolute inset-0 size-full object-cover"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

function ThemedPlaceholder({
  variant, alt, className,
}: { variant: RoutePhotoVariant; alt: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 120 70"
      className={className}
      role="img"
      aria-label={alt}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={`rp-${variant}-bg`} x1="0" y1="0" x2="0" y2="1">
          {variant === "coastal" ? (
            <>
              <stop offset="0%" stopColor="hsl(var(--primary) / 0.18)" />
              <stop offset="100%" stopColor="hsl(var(--pleasant) / 0.25)" />
            </>
          ) : variant === "freeway" ? (
            <>
              <stop offset="0%" stopColor="hsl(var(--muted))" />
              <stop offset="100%" stopColor="hsl(var(--muted-foreground) / 0.18)" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="hsl(var(--muted))" />
              <stop offset="100%" stopColor="hsl(var(--muted) / 0.6)" />
            </>
          )}
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="120" height="70" rx="6" fill={`url(#rp-${variant}-bg)`} />

      {variant === "coastal" && (
        <>
          {/* horizon + waves */}
          <path d="M0 38 L120 32 L120 70 L0 70 Z" fill="hsl(var(--primary) / 0.22)" />
          <path d="M0 50 Q30 46 60 50 T120 48 L120 70 L0 70 Z" fill="hsl(var(--primary) / 0.32)" />
          {/* coastal road */}
          <path d="M8 60 Q35 52 55 44 T112 18"
            stroke="hsl(var(--primary))" strokeWidth="2.5"
            fill="none" strokeLinecap="round" />
        </>
      )}

      {variant === "freeway" && (
        <>
          {/* lane grid */}
          <g stroke="hsl(var(--border))" strokeWidth="1">
            <line x1="0" y1="22" x2="120" y2="22" />
            <line x1="0" y1="48" x2="120" y2="48" />
          </g>
          <path d="M10 58 L40 58 L40 22 L110 22 L110 14"
            stroke="hsl(var(--muted-foreground))" strokeWidth="2.5"
            fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}

      {variant === "generic" && (
        <path d="M10 55 Q40 40 70 35 T112 18"
          stroke="hsl(var(--muted-foreground))" strokeWidth="2.5"
          fill="none" strokeLinecap="round" />
      )}

      {/* endpoint dots */}
      <circle cx="10" cy={variant === "freeway" ? 58 : 60} r="2.5" fill="hsl(var(--foreground))" />
      <circle cx={variant === "coastal" ? 112 : 110} cy={variant === "freeway" ? 14 : 18} r="2.5" fill="hsl(var(--foreground))" />
    </svg>
  );
}
